import React, { CSSProperties, FC, ReactElement, useMemo } from 'react';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import useGetAlert from 'src/api/hooks/useGetAlert';
import { useRouter } from 'next/router';
import Skeleton from 'react-loading-skeleton';
import { delayWrapper } from 'src/utils/delayWrapper';
import { FirstCategoryLevel } from '../../api/types';
import useToTopButtonState from '../../hooks/useToTopButtonState';

const ToTopButton = dynamic(() => import('../ToTopButton/ToTopButton'), {
  ssr: false
});

const NeedHelpWidget = dynamic(
  () => import('../NeedHelpWidget/NeedHelpWidget'),
  {
    ssr: false
  }
);

const Header = dynamic(() => import('../Header/Header'));

const Banner = dynamic(() => import('./Banner/banner'), {
  ssr: false
});

const Footer = dynamic(() => delayWrapper(import('../Footer/Footer')), {
  loading: () => <Skeleton width={'100%'} height={300} />
});

interface Props {
  children: ReactElement | ReactElement[];
  className?: string;
  style?: CSSProperties;
  initialCategory?: FirstCategoryLevel;
  selectedSubCategoryId?: string;
  letters?: { translitName: string; name: string }[];
  toTopButtonProps?: {
    classNames?: string;
  };
}

// This component can be used for page unifying and tracking purposes
const Page: FC<Props> = ({
  children,
  className,
  style = {},
  initialCategory = null,
  selectedSubCategoryId = null,
  letters = [],
  toTopButtonProps = { classNames: '' },
  ...otherProps
}) => {
  const { pathname } = useRouter();
  const isHomePage = pathname === '/';
  const isToTopButtonVisible = useToTopButtonState();
  const { data: alertResponseData } = useGetAlert();

  const headerMemoized = useMemo(
    () => (
      <Header
        initialCategory={initialCategory}
        selectedSubCategoryId={selectedSubCategoryId}
      />
    ),
    [selectedSubCategoryId, JSON.stringify(initialCategory)]
  );

  return (
    <>
      {headerMemoized}
      <Banner />
      <div
        style={style}
        className={cn(
          className,
          'flex flex-col grow min-h-screen',
          !isHomePage || alertResponseData?.alertMessage
            ? 'pt-[112px] sm:pt-[62px]'
            : 'pt-[62px]'
        )}
        {...otherProps}
      >
        {children}
      </div>
      <NeedHelpWidget
        topButton={
          isToTopButtonVisible ? (
            <ToTopButton classNames={toTopButtonProps.classNames} isAbsolute />
          ) : undefined
        }
      />

      <Footer letters={letters} />
    </>
  );
};

export default Page;
